import { useRef, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import dayjs from "dayjs";

import { InboundLayout } from "ui-components/Layout";
import {
  InboundSubmitButton,
  InboundDetailAddButton,
  InboundDetailEditButton,
} from "ui-components/Form";

import { headerRender, headerValues, headerValidation } from "./co-schema";
import { detailRender, detailValues, detailValidation } from "./co-details-schema";
import { useCustomerValue } from "providers/CustomerContext";
import CODetail from "./components/CODetail";
import { Success, Error_ } from "ui-components/Alert";
import { InfoDialog } from "ui-components/Alert";
import { skuValidation } from "layouts/views/common/validation";
import { useMutation } from "@tanstack/react-query";
import { createCO } from "services/data.inbound.service";
import CO from "./components/CO";
import { coColumns } from "./columns";
import BasicTable2 from "ui-components/tables/BasicTable2";
import { InboundHeaderCard } from "ui-components/Layout";
import { coSkuValidation_, deleteDetail, getTotalQuantity, updateDetail } from "../common/common";
import { COCaption } from "../components/Caption";
import { useNavigate } from "react-router-dom";
import { useVendorOptionsWithName } from "hooks/useQueryHook";

const defaultValues = {
  ...headerValues,
  coDate: dayjs(new Date()).format("YYYY-MM-DD"),
  status: "Open",
};

function AddCO() {
  const navigate = useNavigate();
  const { customerCode } = useCustomerValue();
  const [vendorOptions, setVendorOptions] = useState([]);
  const formValues = { ...defaultValues, customerCode };
  const [alert, setAlert] = useState(null);
  const [details, setDetails] = useState([]);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const createCOMutation = useMutation(createCO, {});

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(headerValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
    trigger,
  } = form;

  const [vendorCode] = watch(["vendorCode"]);

  useVendorOptionsWithName({
    customerCode,
    onSuccess: (data) => {
      const vendors = data.data.data;
      if (vendors.length > 0) {
        setValue("vendorCode", vendors[0].vendorCode);
        setValue("cgCompany", vendors[0].vendShortName);
      }
      setVendorOptions(vendors.map((x) => x.vendorCode));
    },
    onError,
  });

  const headerProps = { headerRender, vendorOptions, customerCode, control, setValue, errors };

  const detailProps = { details, setDetails, values: { customerCode, vendorCode } };

  const onSubmit = async () => {
    window.scrollTo(0, 0);

    const isValid = await trigger();
    if (!isValid) return;

    if (details.length === 0) {
      setAlert(<Error_ content="Invalid CO Details" onChange={setAlert} />);
      return;
    }

    const data = getValues();

    const req = { ...data, coDetails: details.map((detail) => ({ ...detail, status: "O" })) };

    createCOMutation.mutate(req, {
      onSuccess: (data) => navigate("/inbound/co", { state: data.data.message }),
      onError,
    });
  };
  return (
    <InboundLayout lg={12}>
      {alert}
      <InboundHeaderCard>
        <form noValidate>
          <CO {...headerProps} />
        </form>
      </InboundHeaderCard>

      <Detail {...detailProps} />
      <InboundSubmitButton
        label="Submit"
        onClick={onSubmit}
        disabled={details.length === 0 || isSubmitting}
      />
    </InboundLayout>
  );
}

const Detail = ({ details, setDetails, values }) => {
  const columns = [
    {
      field: "actions",
      headerName: "Action",
      renderActions: ["Delete"],
      type: "actions",
    },
    ...coColumns,
  ];
  const [alert, setAlert] = useState(null);

  const [formValues, setFormValues] = useState(null);
  const [dialog, setDialog] = useState(null);
  const lineNo = formValues ? formValues.lineNo : details.length + 1;
  const doRef = useRef(null);
  const returnCodeRef = useRef(null);
  const custNbrRef = useRef(null);

  const form = useForm({
    defaultValues: { ...detailValues, lineNo },
    values: formValues || {
      ...detailValues,
      lineNo,
      status: "Open",
      doNumber: doRef.current,
      returnCode: returnCodeRef.current,
      custOrdNbr: custNbrRef.current,
    },
    resolver: yupResolver(detailValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isDirty },
    setValue,
    getValues,
    handleSubmit,
    reset,
  } = form;
  const [skuCode] = watch(["skuCode"]);

  const detailProps = {
    detailRender,
    values: { ...values, skuCode },
    control,
    errors,
    setValue,
    getValues,
  };
  const onSubmit = (data) => {
    const isSKUDuplicate = coSkuValidation_(details, data);
    if (isSKUDuplicate) {
      setAlert(
        <Error_ content="Duplicate SKU Code, Customer Order Number, MLN" onChange={setAlert} />
      );
      return;
    }
    doRef.current = data.doNumber;
    returnCodeRef.current = data.returnCode
    custNbrRef.current = data.custOrdNbr
    if (formValues === null) {
      setDetails([...details, data]);
      reset();
      setAlert(<Success content="Added" onChange={setAlert} />);
    } else {
      const newDetails = updateDetail(details, data);
      setDetails(newDetails);
      setFormValues(null);
      setAlert(<Success content="Updated" onChange={setAlert} />);
    }
  };
  const onRowClick = (values) => {
    if (formValues !== null && isDirty) {
      setDialog(
        <InfoDialog
          title={`Unsaved changes for Line No ${formValues.lineNo}`}
          content="Do you want to save the changes"
          onOk={() => {
            const data = getValues();
            const newDetails = details.map((detail) => {
              if (detail.lineNo === formValues.lineNo) return data;
              return detail;
            });
            setDetails(newDetails);
            setDialog(null);
            setFormValues(values);
          }}
          onClose={() => {
            setDialog(null);
            setFormValues(values);
          }}
        />
      );
    } else {
      setFormValues(values);
    }
    return true;
  };
  const handleDelete = (data) => {
    const newDetails = deleteDetail(details, data);
    setDetails(newDetails);
    setAlert(<Success content="Deleted" onChange={setAlert} />);
  };

  const totalQuantity = getTotalQuantity(details, "coQty");
  const Caption = <COCaption totalRows={details.length} totalQuantity={totalQuantity} />;
  return (
    <InboundHeaderCard>
      {dialog}
      {alert}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <CODetail {...detailProps} />
        {formValues === null ? (
          <InboundDetailAddButton />
        ) : (
          <InboundDetailEditButton disabled={!isDirty} onClick={() => setFormValues(null)} />
        )}
      </form>
      <BasicTable2
        columns={columns}
        caption={Caption}
        rows={details}
        onRowClick={onRowClick}
        onDelete={handleDelete}
      />
    </InboundHeaderCard>
  );
};

export default AddCO;
